import { useMemo, useCallback, useState, ChangeEvent, Dispatch, SetStateAction } from 'react'

import { SortDescriptor, orderBy, process } from '@progress/kendo-data-query'
import MD5 from 'crypto-js/md5'
import { v4 as makeUuid } from 'uuid'

import { GridSortChangeEvent } from '@progress/kendo-react-grid'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import {
  AVAILABLE_ATTACHMENT_FILTER_ACTIONS,
  AttachmentFilterType,
  AttachmentFilter,
  AttachmentFilterAction,
  AvailableSettings
} from 'types/Settings'
import routesConfig from 'lib/routesConfig'

import { updateAccountSettings, updateDomainSettings } from 'redux/features/settings/settingsSlice'
import { useInboundSettingsRights } from 'components/libs/userRights/pages/useInboundSettingsRights'
import {
  AttachmentFilterPolicyForm,
  AttachmentFilterPolicyFormState,
  useAttachmentFilterPolicyForm
} from 'components/pages/inboundSettings/contentPolicies/useAttachmentFilterPolicyForm'

export interface State {
  isConfirmDialogOpen: boolean
  sort: SortDescriptor[]
  error: string
  actionOptions: string[]
  form: AttachmentFilterPolicyForm
  formState: AttachmentFilterPolicyFormState
  tableData: {
    total: number
    data: AttachmentFilter[]
  }
}

export interface EventHandlers {
  onAdd: () => void
  onRemove: (id: string) => void
  onRemoveConfirm: () => void
  onRemoveCancel: () => void
  onBulkEdit: () => void
  handleSortChange: (e: GridSortChangeEvent) => void
  handleOnInputChange: (e: ChangeEvent<HTMLInputElement>) => void
  handleOnSelectChange: (e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => void
}

export type UseAttachmentTableLogic = [State, EventHandlers]

const INITIAL_FORM_STATE = {
  type: AttachmentFilterType.FILENAME,
  pattern: '',
  scanArchive: '0',
  action: AttachmentFilterAction.BLOCK,
  comment: ''
}

const BLANK_ENTRY = {
  ...INITIAL_FORM_STATE,
  id: ''
}

export const enum SELECT_NAMES {
  TYPE = 'type',
  SCAN = 'scan_archive',
  ACTION = 'action'
}

export interface UseAttachmentTableLogicProps {
  delegateDirtyFormState: Dispatch<SetStateAction<boolean>>
}

const initialSort = [{ field: 'pattern', dir: 'asc' } as SortDescriptor]

export const useAttachmentTableLogic = ({
  delegateDirtyFormState
}: UseAttachmentTableLogicProps): UseAttachmentTableLogic => {
  const form = useAttachmentFilterPolicyForm({
    initialState: INITIAL_FORM_STATE,
    delegateIsDirtyForm: delegateDirtyFormState
  })
  const dispatch = useAppDispatch()
  const [sort, setSort] = useState(initialSort)
  const [removeId, setRemoveId] = useState<string>('')
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false)

  const { accessTokenObject, accountSettings, domainSettings } = useAppSelector(_stores => ({
    accessTokenObject: _stores.auth.accessTokenObject,
    accountSettings: _stores.settings.accountSettings,
    domainSettings: _stores.settings.domainSettings
  }))
  const { canQuarantineSettings } = useInboundSettingsRights()

  const tableData = useMemo(() => {
    const settings = accessTokenObject?.pdDomainId ? domainSettings : accountSettings
    const parsedData = settings.attachment_filter_policy
      ? (JSON.parse(settings.attachment_filter_policy as string) as AttachmentFilter[])
      : []
    const orderedData = orderBy(parsedData || [], sort)
    const { data } = process(orderedData, {})

    // Have the first item as an edit item.  This method works with sorting.
    data.unshift(BLANK_ENTRY)
    return {
      data,
      total: data.length || 0
    }
  }, [accessTokenObject, domainSettings, accountSettings, sort])

  const updateData = useCallback(
    (data: AttachmentFilter[]) => {
      if (accessTokenObject?.pdDomainId) {
        dispatch(
          updateDomainSettings({
            domainId: accessTokenObject.pdDomainId,
            settings: { [AvailableSettings.ATTACHMENT_FILTER_POLICY]: data }
          })
        )
      } else {
        dispatch(updateAccountSettings({ settings: { [AvailableSettings.ATTACHMENT_FILTER_POLICY]: data } }))
      }
    },
    [dispatch, accessTokenObject]
  )

  const onAdd = useCallback(() => {
    const data = tableData.data.filter(entry => entry.id !== '')
    const newEntry: AttachmentFilter = {
      ...form.getState(),
      scanArchive: Number(form.fields.scanArchive.getValue()),
      minSize: 0,
      id: MD5(makeUuid()).toString()
    }
    if (form.validate(newEntry, { data })) {
      updateData([...data, newEntry])
    }
  }, [form, tableData.data, updateData])

  const onRemove = useCallback(
    (id: string) => {
      setRemoveId(id)
      setIsConfirmDialogOpen(true)
    },
    [setRemoveId, setIsConfirmDialogOpen]
  )

  const onRemoveConfirm = useCallback(() => {
    const policies = tableData.data.filter(entry => entry.id !== removeId && entry.id !== '')
    updateData(policies)
    setRemoveId('')
    setIsConfirmDialogOpen(false)
  }, [tableData.data, updateData, removeId])

  const onRemoveCancel = useCallback(() => {
    setRemoveId('')
    setIsConfirmDialogOpen(false)
  }, [setRemoveId, setIsConfirmDialogOpen])

  const onBulkEdit = useCallback(() => {
    routesConfig.INBOUND_SETTINGS_ATTACHMENT_FILTERS_BULK_EDIT.goto()
  }, [])

  const handleOnInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      // eslint-disable-next-line default-case
      switch (true) {
        case name === 'pattern':
          form.fields.pattern.setValue(value)
          break
        case name === 'comment':
          form.fields.comment.setValue(value)
          break
      }
      form.delegateIsDirty()
    },
    [form]
  )

  const handleOnSelectChange = useCallback(
    (e: ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
      const { name, value } = e.target
      if (typeof value === 'string') {
        // eslint-disable-next-line default-case
        switch (name) {
          case SELECT_NAMES.TYPE:
            form.fields.type.setValue(value)
            break
          case SELECT_NAMES.SCAN:
            form.fields.scanArchive.setValue(value)
            break
          case SELECT_NAMES.ACTION:
            form.fields.action.setValue(value)
            break
        }
        form.delegateIsDirty()
      }
    },
    [form]
  )

  const handleSortChange = useCallback((e: GridSortChangeEvent) => {
    setSort(e.sort)
  }, [])

  const actionOptions = useMemo(
    () =>
      canQuarantineSettings ? AVAILABLE_ATTACHMENT_FILTER_ACTIONS : AVAILABLE_ATTACHMENT_FILTER_ACTIONS.slice(0, -1),
    [canQuarantineSettings]
  )

  return useMemo(
    () => [
      {
        isConfirmDialogOpen,
        sort,
        error: form.error,
        actionOptions,
        form,
        formState: form.getState(),
        tableData
      },
      {
        onAdd,
        onRemove,
        onRemoveConfirm,
        onRemoveCancel,
        onBulkEdit,
        handleSortChange,
        handleOnInputChange,
        handleOnSelectChange
      }
    ],
    [
      isConfirmDialogOpen,
      sort,
      form,
      actionOptions,
      tableData,
      onAdd,
      onRemove,
      onRemoveConfirm,
      onRemoveCancel,
      onBulkEdit,
      handleSortChange,
      handleOnInputChange,
      handleOnSelectChange
    ]
  )
}
